/**
 * Snapshot Diff Card — shows an AI-generated human-friendly change
 * summary between two Business OS snapshots. Reused by:
 *   - Business OS Dashboard (expandable per snapshot row)
 *   - Snapshot History (dedicated modal)
 *   - Business OS Portal (top of the shared view)
 */
import React from 'react';
import { Sparkle, ArrowRight, CircleNotch } from '@phosphor-icons/react';

const CATEGORIES = [
  { key: 'new_marketing', label: 'New marketing', color: '#F97316' },
  { key: 'sales_assets', label: 'New sales assets', color: '#22C55E' },
  { key: 'plan_improvements', label: 'Business plan', color: '#3B82F6' },
  { key: 'updated_branding', label: 'Branding updates', color: '#A855F7' },
  { key: 'ops_improvements', label: 'Operational updates', color: '#EAB308' },
  { key: 'new_assets', label: 'All new assets', color: '#94A3B8' },
];

export const SnapshotDiffCard = ({ diff, loading, accentColor = '#22C55E', theme = 'dark', testid = 'snapshot-diff-card' }) => {
  const isDark = theme === 'dark';
  const cardCls = isDark
    ? 'rounded-2xl border border-slate-800 bg-slate-900/70 p-5 text-slate-100'
    : 'rounded-2xl border border-slate-200 bg-white p-5 text-slate-800 shadow-sm';
  const subCls = isDark ? 'text-slate-400' : 'text-slate-500';
  const chipBg = isDark ? 'bg-slate-800/70' : 'bg-slate-100';

  if (loading) {
    return (
      <div className={cardCls} data-testid={`${testid}-loading`}>
        <div className="flex items-center gap-2 text-sm">
          <CircleNotch size={16} className="animate-spin" />
          <span>Analyzing changes with AI…</span>
        </div>
      </div>
    );
  }

  if (!diff || !diff.ai_summary) {
    return (
      <div className={cardCls} data-testid={testid}>
        <div className={`text-sm ${subCls}`}>No changes to summarise yet.</div>
      </div>
    );
  }

  const ai = diff.ai_summary;
  const fromV = diff.from_version ?? diff.structural?.from_version ?? 0;
  const toV = diff.to_version ?? diff.structural?.to_version ?? '?';

  return (
    <div className={cardCls} data-testid={testid}>
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: accentColor + '20', color: accentColor }}
        >
          <Sparkle size={18} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[11px] uppercase tracking-widest ${subCls} mb-0.5`}>
            v{fromV} <ArrowRight size={10} className="inline align-middle" /> v{toV} · AI change summary
          </div>
          <div className={`text-base font-semibold leading-snug ${isDark ? 'text-white' : 'text-slate-900'}`} data-testid={`${testid}-headline`}>
            {ai.headline || 'No changes'}
          </div>
        </div>
      </div>
      {ai.tldr && (
        <div className={`text-sm leading-relaxed mb-4 ${isDark ? 'text-slate-300' : 'text-slate-600'}`} data-testid={`${testid}-tldr`}>
          {ai.tldr}
        </div>
      )}
      <div className="space-y-2.5">
        {CATEGORIES.map(({ key, label, color }) => {
          const items = ai[key] || [];
          if (!items.length) return null;
          return (
            <div key={key} className="flex gap-3" data-testid={`${testid}-cat-${key}`}>
              <div
                className="w-1 rounded-full flex-shrink-0"
                style={{ background: color }}
              />
              <div className="flex-1 min-w-0">
                <div className={`text-[11px] uppercase tracking-wider ${subCls} mb-1`}>{label}</div>
                <div className="flex flex-wrap gap-1.5">
                  {items.slice(0, 8).map((it, idx) => (
                    <span
                      key={`${key}-${idx}-${it.slice(0, 12)}`}
                      className={`inline-block ${chipBg} rounded-md px-2 py-1 text-xs`}
                    >
                      {it}
                    </span>
                  ))}
                  {items.length > 8 && (
                    <span className={`text-xs ${subCls}`}>+{items.length - 8} more</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {!CATEGORIES.some(({ key }) => (ai[key] || []).length > 0) && (
          <div className={`text-sm ${subCls}`}>
            No new approved assets since v{fromV}.
          </div>
        )}
      </div>
    </div>
  );
};

export default SnapshotDiffCard;
